import { RestApi } from "infrontjs";

/**
 * Thin wrapper around IF.RestApi for talking to the BELTURO core-api.
 * Three frontends, one API — the guest-app is just another client of the
 * shared backend. The active tenant is resolved server-side from the
 * subdomain (sent as the `x-tenant` header), so business logic never has to
 * filter by tenant per call.
 */
export class Api
{
    constructor( app )
    {
        this.app = app;

        const apiUrl = this.app.config.get( 'api' );

        this._api = new RestApi(
            apiUrl,
            { "Content-Type": "application/json" }
        );
        this._api.headers.set( 'x-version', this.app.config.get( 'version' ) );

        // Scope every request to the current tenant (subdomain slug).
        const tenant = this.app.theme.getTenantSlug();
        if ( tenant )
        {
            this._api.headers.set( 'x-tenant', tenant );
        }

        // Opens a draft house to this tab (core/Preview).
        const previewToken = this.app.preview?.getToken();
        if ( previewToken )
        {
            this._api.headers.set( 'x-preview', previewToken );
        }
    }

    getUrl()
    {
        return this._api.url;
    }

    setSessionToken( sessionToken )
    {
        this._api.headers.set( 'x-sessiontoken', sessionToken );
    }

    deleteSessionToken()
    {
        this._api.headers.delete( 'x-sessiontoken' );
    }

    async get( endpoint )
    {
        return this._call( () => this._api.get( endpoint ) );
    }

    async post( endpoint, params = {} )
    {
        return this._call( () => this._api.post( endpoint, params ) );
    }

    async delete( endpoint )
    {
        return this._call( () => this._api.delete( endpoint ) );
    }

    async put( endpoint, params = {} )
    {
        return this._call( () => this._api.put( endpoint, params ) );
    }

    async patch( endpoint, params = {} )
    {
        return this._call( () => this._api.patch( endpoint, params ) );
    }

    /**
     * A request that failed without an HTTP answer (RestApi sets `status` on
     * HTTP errors only) may mean the network is gone — Connectivity finds out
     * and tells the guest. The error itself still goes to the caller unchanged.
     */
    async _call( request )
    {
        try
        {
            return this._processResult( await request() );
        }
        catch ( e )
        {
            if ( e && e.status === undefined && e.name !== "AbortError" && this.app.connectivity )
            {
                this.app.connectivity.reportFailure();
            }
            throw e;
        }
    }

    _processResult( result )
    {
        if ( result && result.hasOwnProperty( 'status' ) )
        {
            console.log( result );
        }
        return result;
    }
}
